/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useRef, useState } from 'react';

import { styled } from '@uhg-abyss/web/tools/styled';
import { Text } from '@uhg-abyss/web/ui/Text';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import dayjs from 'dayjs';

import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { BaseTextInputNode } from './BaseTextInput';

const SCREEN_HEIGHT = window.innerHeight;

const ModalOverlay = styled('div', {
  flex: 1,
  justifyContent: 'flex-end',
  backgroundColor: 'rgba(0,0,0,0.45)',
});

// — Bottom sheet card
const ModalSheet = styled('div', {
  borderRadius: '$core.border-radius.xxl',
  padding: '$core.spacing.200',
  shadowOpacity: 0.22,
  elevation: 20,
  backgroundColor: '$core.color.neutral.0',
  shadowColor: '$core.color.neutral.80',
  marginHorizontal: '$core.spacing.200',
  marginBottom: '$core.spacing.300',
  height: SCREEN_HEIGHT * 0.9,
});

const DEFAULT_SELECT_INPUT_STYLES: Record<string, unknown> = {
  'abyss-select-input-root': {
    marginVertical: '$core.spacing.100',
  },
  'abyss-select-input-label': {
    fontSize: '$core.font-size.p.80',
    fontWeight: '$core.font-weight.bold',
    color: '$core.color.neutral.90',
  },
  'abyss-select-input-container': {
    minHeight: '$core.sizing.600',
    borderWidth: 1,
    borderColor: '$core.color.neutral.30',
    borderRadius: '$core.border-radius.md',
    backgroundColor: '$core.color.neutral.0',
  },
  'abyss-select-input-text': {
    fontSize: '$core.font-size.p.80',
    fontWeight: '$core.font-weight.bold',
    color: '$core.color.neutral.100',
  },
  'abyss-input-message-text': {
    color: '$core.color.red.100',
  },
};

const SelectInputNode: React.FC<React.ComponentProps<typeof SelectInput>> = ({
  style,
  ...props
}) => (
  <SelectInput
    {...props}
    style={{
      ...DEFAULT_SELECT_INPUT_STYLES,
      ...(style ?? {}),
    }}
  />
);

const ModalHeader = styled('div', {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '$core.spacing.200',
  marginTop: '$core.spacing.400',
});

const ModalTitleText = styled(Text, {
  fontSize: '$core.font-size.h.40',
  fontWeight: '$core.font-weight.bold',
  color: '$core.color.brand.100',
});

const ModalCloseButton = styled('button', {
  padding: '$core.spacing.100',
  borderRadius: '$core.border-radius.xxl',
  borderColor: '$core.color.neutral.40',
  borderWidth: 1,
  width: '$core.sizing.400',
  height: '$core.sizing.400',
  justifyContent: 'center',
  alignItems: 'center',
});

// — Confirm / cancel action buttons
const ModalActionRow = styled('div', {
  flexDirection: 'row',
  gap: '$core.spacing.150',
  marginTop: '$core.spacing.200',
});

const CancelButton = styled('button', {
  flex: 1,
  paddingVertical: '$core.spacing.150',
  paddingHorizontal: '$core.spacing.200',
  borderRadius: '$core.border-radius.md',
  borderWidth: 1,
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '$core.sizing.600',
  borderColor: '$core.color.brand.80',
});

const SaveButton = styled('button', {
  flex: 1,
  paddingVertical: '$core.spacing.150',
  paddingHorizontal: '$core.spacing.200',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: '$core.border-radius.md',
  minHeight: '$core.sizing.600',
  backgroundColor: '$core.color.brand.80',
});

// — Text nodes
const CloseButtonIcon = styled(Text, {
  fontSize: '$core.font-size.p.100',
  fontWeight: '$core.font-weight.medium',
  color: '$core.color.neutral.80',
});

const CancelButtonLabel = styled(Text, {
  fontSize: '$core.font-size.p.80',
  fontWeight: '$core.font-weight.bold',
  color: '$core.color.brand.80',
});

const SaveButtonLabel = styled(Text, {
  fontSize: '$core.font-size.p.80',
  fontWeight: '$core.font-weight.bold',
  color: '$core.color.neutral.0',
});

const SaveButtonIcon = styled(Text, {
  fontSize: '$core.font-size.p.80',
  fontWeight: '$core.font-weight.bold',
  color: '$core.color.neutral.1',
});

const FieldRow = styled('div', {
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '$core.spacing.150',
});

const FieldColumn = styled('div', {
  flex: 1,
  justifyContent: 'flex-start',
});

type FormData = Partial<Record<string, string>>;
type FormErrors = Partial<Record<string, string>>;

const areRecordsEqual = (
  first: Partial<Record<string, string>>,
  second: Partial<Record<string, string>>,
): boolean => {
  const firstKeys = Object.keys(first);
  const secondKeys = Object.keys(second);

  if (firstKeys.length !== secondKeys.length) {
    return false;
  }

  return firstKeys.every((key) => first[key] === second[key]);
};

export interface DynamicFormModalProps {
  isVisible: boolean;
  onClose: () => void;
  formConfig: DynamicFormConfig;
  onSaveClick: (formData: FormData) => void;
}

interface DynamicFormValidationRule {
  regEx: string;
  message: string;
}

interface DynamicFormSelectOption {
  label: string;
  value: string;
}

interface DynamicFormField {
  label?: string;
  type: 'input' | 'date' | 'select';
  placeholder?: string;
  minDate?: string;
  maxDate?: string;
  validations?: DynamicFormValidationRule[];
  options?: {
    items?: DynamicFormSelectOption[];
  };
  items?: DynamicFormSelectOption[];
}

interface DynamicFormConfig {
  title?: string;
  cancelButton?: string;
  saveButton?: string;
  formFields?: Record<string, DynamicFormField>;
  initialValues?: FormData;
}

const DEFAULT_DATE_INPUT_STYLES: Record<string, unknown> = {
  'abyss-date-input-root': {
    marginVertical: '$core.spacing.100',
  },
  'abyss-date-input-label': {
    fontSize: '$core.font-size.p.80',
    fontWeight: '$core.font-weight.bold',
    color: '$core.color.neutral.90',
  },
  'abyss-date-input': {
    fontSize: '$core.font-size.p.80',
    fontWeight: '$core.font-weight.bold',
    color: '$core.color.neutral.100',
  },
  'abyss-input-message-text': {
    color: '$core.color.red.100',
  },
};

const DateInputNode: React.FC<React.ComponentProps<typeof DateInput>> = ({ style, ...props }) => (
  <DateInput
    {...props}
    style={{
      ...DEFAULT_DATE_INPUT_STYLES,
      // 'abyss-form-input-border': {
      //   borderColor: errorMessage ? '$core.color.red.100' : '$core.color.neutral.30',
      //   borderWidth: 1,
      //   borderRadius: '$core.border-radius.md',
      //   overflow: 'hidden',
      // },
      // 'abyss-form-input-focus-border': {
      //   borderColor: 'transparent',
      //   borderWidth: 0,
      //   borderRadius: '$core.border-radius.md',
      //   shadowOpacity: 0,
      //   elevation: 0,
      // },
      ...(style ?? {}),
    }}
  />
);

const formatDateToMMDDYYYY = (value?: Date): string => {
  if (!value) {
    return '';
  }

  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  const year = String(value.getFullYear());

  return `${month}/${day}/${year}`;
};

const getSingleSelectValue = (
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- matches abyss SelectInput onChange value type
  value: Function | string[] | number[] | string | number,
): string => {
  if (Array.isArray(value)) {
    return value[0] ? String(value[0]) : '';
  }

  if (typeof value === 'function') {
    return '';
  }

  return String(value);
};

const getDateInputValue = (value?: string): string => {
  const parsedDate = parseDateValue(value);

  if (!parsedDate) {
    return '';
  }

  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  const year = String(parsedDate.getFullYear());

  return `${year}-${month}-${day}`;
};

const getChangedDate = (value: React.ChangeEvent<HTMLInputElement> | Date): Date | undefined => {
  if (value instanceof Date) {
    return value;
  }

  const inputValue = value.currentTarget.value;

  return inputValue ? new Date(`${inputValue}T00:00:00`) : undefined;
};

const parseDateValue = (value?: string): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const mmDdYyyyMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (mmDdYyyyMatch) {
    const month = Number(mmDdYyyyMatch[1]);
    const day = Number(mmDdYyyyMatch[2]);
    const year = Number(mmDdYyyyMatch[3]);
    const parsedDate = new Date(year, month - 1, day);

    if (
      parsedDate.getFullYear() === year &&
      parsedDate.getMonth() === month - 1 &&
      parsedDate.getDate() === day
    ) {
      return parsedDate;
    }

    return undefined;
  }

  const fallbackDate = new Date(value);
  if (Number.isNaN(fallbackDate.getTime())) {
    return undefined;
  }

  return fallbackDate;
};

/* ---------- COMPONENT ---------- */
const DynamicFormModal: React.FC<DynamicFormModalProps> = ({
  isVisible,
  onClose,
  formConfig,
  onSaveClick,
}) => {
  const [formData, setFormData] = useState<FormData>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const wasVisibleRef = useRef(false);
  const {
    title: configTitle,
    cancelButton,
    saveButton,
    formFields = {},
    initialValues = {},
  } = formConfig;
  const orderedFieldNames = Object.keys(formFields);

  const resetForm = () => {
    setFormData({});
    setErrors({});
  };

  useEffect(() => {
    if (isVisible) {
      resetForm();
    }
  }, [isVisible]);

  useEffect(() => {
    const justOpened = isVisible && !wasVisibleRef.current;
    wasVisibleRef.current = isVisible;

    if (!justOpened) {
      return;
    }

    // const nextDefaults = orderedFieldNames.reduce<FormData>(
    //   (accumulator, fieldName) => {
    //     return accumulator;
    //   },
    //   {}
    // );

    const nextFormData = { ...initialValues };
    const nextErrors = Object.keys(formFields).reduce<FormErrors>((accumulator, fieldName) => {
      accumulator[fieldName] = '';
      return accumulator;
    }, {});

    setFormData((prev) => (areRecordsEqual(prev, nextFormData) ? prev : nextFormData));
    setErrors((prev) => (areRecordsEqual(prev, nextErrors) ? prev : nextErrors));
  }, [formFields, initialValues, isVisible, orderedFieldNames]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateField = (field: string) => {
    const validations = formFields?.[field]?.validations ?? [];
    const value = formData[field] ?? '';
    let fieldError = '';
    for (const rule of validations) {
      if (!value.match(new RegExp(rule.regEx))) {
        fieldError = rule.message;
        break;
      }
    }
    setErrors((prev) => ({ ...prev, [field]: fieldError }));
  };

  const renderField = (fieldName: string) => {
    const field = formFields[fieldName];

    if (!field) {
      return null;
    }

    const options = field.options?.items ?? field.items ?? [];
    if (field.type === 'select') {
      return (
        <FieldRow key={fieldName}>
          <FieldColumn>
            <SelectInputNode
              placeholder={field.placeholder ?? field.label ?? 'Select an option'}
              label={field.label ?? fieldName}
              value={formData[fieldName] ?? ''}
              onChange={(value) => {
                handleChange(fieldName, getSingleSelectValue(value));
              }}
              options={options}
              errorMessage={errors[fieldName]}
            />
          </FieldColumn>
        </FieldRow>
      );
    }

    if (field.type === 'date') {
      return (
        <FieldRow key={fieldName}>
          <FieldColumn>
            <DateInputNode
              label={field.label ?? 'Date'}
              value={getDateInputValue(formData[fieldName])}
              minDate={field.minDate ? dayjs(field.minDate) : undefined}
              maxDate={field.maxDate ? dayjs(field.maxDate) : undefined}
              onChange={(value) => {
                handleChange(fieldName, formatDateToMMDDYYYY(getChangedDate(value)));
              }}
              errorMessage={errors[fieldName]}
            />
          </FieldColumn>
        </FieldRow>
      );
    }

    return (
      <BaseTextInputNode
        key={fieldName}
        testID={`${fieldName}Input`}
        placeholder={field.placeholder}
        label={field.label ?? fieldName}
        value={formData[fieldName] ?? ''}
        onChangeText={(value) => handleChange(fieldName, value)}
        onBlur={() => validateField(fieldName)}
        errorMessage={errors[fieldName]}
      />
    );
  };

  const validateAll = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;
    orderedFieldNames.forEach((fieldName) => {
      const validations = formFields?.[fieldName]?.validations ?? [];
      const value = formData[fieldName] ?? '';
      for (const rule of validations) {
        if (!value.match(new RegExp(rule.regEx))) {
          newErrors[fieldName] = rule.message;
          isValid = false;
          break;
        }
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  return (
    <ModalDialog
      isOpen={isVisible}
      // transparent
      // animationType="slide"
      onClose={handleClose}
    >
      <ModalOverlay>
        <ModalSheet>
          <ModalHeader>
            <ModalTitleText>{configTitle ?? ''}</ModalTitleText>
            <ModalCloseButton onPress={handleClose}>
              <CloseButtonIcon>✕</CloseButtonIcon>
            </ModalCloseButton>
          </ModalHeader>

          <div style={{ flex: 1 }}>
            <div style={{ overflowY: 'auto', height: '100vh', paddingBottom: 24 }}>
              {orderedFieldNames.map((fieldName) => renderField(fieldName))}
            </div>
          </div>

          {/* Confirm / cancel actions */}
          <ModalActionRow>
            <CancelButton onPress={handleClose}>
              <CancelButtonLabel>{cancelButton}</CancelButtonLabel>
            </CancelButton>
            <SaveButton
              onPress={() => {
                if (validateAll()) {
                  handleClose();
                  onSaveClick(formData);
                }
              }}
            >
              <SaveButtonLabel>{saveButton ?? 'Save'}</SaveButtonLabel>
              <SaveButtonIcon>›</SaveButtonIcon>
            </SaveButton>
          </ModalActionRow>
        </ModalSheet>
      </ModalOverlay>
    </ModalDialog>
  );
};

export { DynamicFormModal };
